import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useSearchParams, Link } from 'react-router-dom'; // Updated import
import { MoveLeft, MoveRight, SignalLow, SignalMedium, SignalHigh } from 'lucide-react'; // Import Lucid icons

const SearchPage = () => {
    const [query, setQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState(''); // Add state to hold the search query
    const [results, setResults] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0); // Track the current index
    const [searchParams, setSearchParams] = useSearchParams();
    const [difficultWords, setDifficultWords] = useState({}); // State to hold difficult words
    const [currentDifficultWordsIndex, setCurrentDifficultWordsIndex] = useState(1);
    const API_URL = process.env.REACT_APP_API_URL; // Add this line to define the API_URL from the environment variable

    const handleSearch = useCallback(async (searchQuery) => { // Wrapped in useCallback
        setSearchQuery(searchQuery);

        if (searchQuery) {
            // Update the URL with the query parameter
            setSearchParams({ q: searchQuery }, { replace: true });
            // Fetch results from the server
            const response = await fetch(`${API_URL}/search?q=${searchQuery}`);
            const data = await response.json();
            setResults(data); // Set the results state with the fetched data
            setCurrentIndex(0); // Reset currentIndex to 0 for new search

            // Update the document title here after the search
            document.title = `How to say ${searchQuery.toUpperCase()}. Listen to the audio pronunciation in English`;
        }
    }, [API_URL, setSearchParams]); // Added query and setSearchParams to dependencies

    useEffect(() => {
        const initialQuery = searchParams.get('q') || ''; // Get query from URL
        setQuery(initialQuery); // Set initial query state
        if (initialQuery) {
            handleSearch(initialQuery); // Perform search if there's an initial query
        }
    }, [searchParams, handleSearch]); // Run effect when searchParams change

    useEffect(() => {
        const fetchDifficultWords = async () => {
            const response = await fetch('https://s3.eu-central-1.amazonaws.com/replay.one/4tkud3b1p0/underrated.json');
            const data = await response.json();
            setDifficultWords(data); // Set the difficult words state with the fetched data
        };

        fetchDifficultWords(); // Call the function to fetch difficult words
    }, []); // Added empty dependency array to run only once on mount

    const handleNext = useCallback(() => { // Wrapped in useCallback
        if (currentIndex < results.length - 1) {
            setCurrentIndex(currentIndex + 1); // Move to the next result
        } else {
            setCurrentIndex(0); // Loop back to the first result
        }
    }, [currentIndex, results]); // Added dependencies

    const handlePrevious = useCallback(() => { // Wrapped in useCallback
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1); // Move to the previous result
        } else {
            setCurrentIndex(results.length - 1); // Loop back to the last result
        }
    }, [currentIndex, results]); // Added dependencies

    const highlightSearchTerm = (text, term) => {
        if (!term) return text; // Return original text if no term is provided
        const regex = new RegExp(`(${term})`, 'gi'); // Create a regex to match the term (case insensitive)
        return text.split(regex).map((part, index) =>
            regex.test(part) ? <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
        );
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default form submission behavior
            handleSearch(query); // Trigger search on Enter key press
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                handlePrevious(); // Navigate to previous result on left arrow key
            } else if (e.key === 'ArrowRight') {
                handleNext(); // Navigate to next result on right arrow key
            }
        };

        window.addEventListener('keydown', handleKeyDown); // Add event listener for keydown

        return () => {
            window.removeEventListener('keydown', handleKeyDown); // Clean up event listener on unmount
        };
    }, [currentIndex, results, handleNext, handlePrevious]); // Added handleNext and handlePrevious to the dependency array

    return (
        <div className="flex flex-col items-center p-4">
            {/* Block 1: Search input field and search button */}
            <div className="flex mb-4 w-full">
                <input
                    type="text"
                    placeholder="Enter your search query"
                    value={query} // This should reflect the current state
                    onChange={(e) => setQuery(e.target.value)} // Update state on change
                    onKeyDown={handleKeyPress} // Ensure this does not trigger unnecessary searches
                    className="flex-grow p-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out"
                />
                <button
                    onClick={() => handleSearch(query)}
                    className="ml-2 p-3 text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 ease-in-out"
                >
                    Search
                </button>
            </div>
            {/* Block 2: YouTube Video Iframe */}
            <div className="flex mb-4 w-full justify-center">
                {results.length > 0 ? (
                    <iframe
                        key={results[currentIndex].video_id}
                        width="384"
                        height="216"
                        src={`https://www.youtube-nocookie.com/embed/${results[currentIndex].video_id}?start=${Math.floor(results[currentIndex].start_time)}&autoplay=1&rel=0&cc_load_policy=1&cc_lang_pref=en`}
                        title="YouTube Video"
                        style={{ border: 'none' }} // Updated to remove frameBorder
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                ) : (
                    // Conditional rendering for "No results found"
                    searchQuery && (
                        <p className="text-center">No results found</p>
                    )
                )}
            </div>
            {/* Block 3: Buttons 'Previous', 'Next' and subtitle text */}
            <div className="">
                {results.length > 0 && (
                    <>
                        <div className="text-center">
                            {highlightSearchTerm(results[currentIndex].subtitle_text, searchQuery)}
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                            <button
                                onClick={handlePrevious}
                                disabled={results.length === 0}
                                className="p-3 text-base rounded-md transition duration-200 ease-in-out"
                                title={currentIndex > 0 ? results[currentIndex - 1].subtitle_text : ''} // Tooltip for previous button
                            >
                                <MoveLeft /> {/* Use Lucid icon for previous */}
                            </button>
                            {/* Counter displaying current index and total results */}
                            <span className="text-base">
                                {currentIndex + 1} of {results.length}
                            </span>
                            <button
                                onClick={handleNext}
                                disabled={results.length === 0}
                                className="p-3 text-base rounded-md transition duration-200 ease-in-out"
                                title={currentIndex < results.length - 1 ? results[currentIndex + 1].subtitle_text : ''} // Tooltip for next button
                            >
                                <MoveRight /> {/* Use Lucid icon for next */}
                            </button>
                        </div>
                    </>
                )}
            </div>
            {/* Block 4: Difficult Words Section */}
            <div className="flex justify-between mb-4"> {/* Use justify-between to align items */}
                <p className=""> {/* Set width to 60% */}
                    <span>We think you might be interested in checking out these words:&nbsp;</span>
                    {difficultWords[currentDifficultWordsIndex]?.map((word, index) => (
                        <span key={index}>
                            <Link to={`/?q=${word}`} className="text-blue-600 hover:underline">
                                {word}
                            </Link>
                        </span>
                    )).reduce((prev, curr) => [prev, ', ', curr]) || ''}.
                </p>
                <div className="flex flex-col justify-center items-end ml-4"> {/* Set width to 40%, center buttons vertically, and align to the right */}
                    <button
                        onClick={() => setCurrentDifficultWordsIndex((prev) => (prev > 1 ? prev - 1 : 3))} // Loop back to last if at first
                        disabled={currentDifficultWordsIndex === 1}
                        className={`p-2 text-base rounded-md transition duration-200 ease-in-out mb-2 ${currentDifficultWordsIndex === 1 ? 'text-gray-400' : ''}`} // Gray out if at first index
                        title={currentDifficultWordsIndex === 2 ? 'Easy words' : (currentDifficultWordsIndex === 3 ? 'Medium words' : '')} // Tooltip for previous button
                    >
                        <MoveLeft /> {/* Use Lucid icon for previous */}
                    </button>
                    {/* Indicator Icon */}
                    {currentDifficultWordsIndex === 1 ? (
                        <span title="Easy words" className="p-2 mb-2"><SignalLow /></span>
                    ) : currentDifficultWordsIndex === 2 ? (
                        <span title="Medium words" className="p-2 mb-2"><SignalMedium /></span>
                    ) : (
                        <span title="Hard words" className="p-2 mb-2"><SignalHigh /></span>
                    )}
                    <button
                        onClick={() => setCurrentDifficultWordsIndex((prev) => (prev < 3 ? prev + 1 : 1))} // Loop back to first if at last
                        disabled={currentDifficultWordsIndex === 3}
                        className={`p-2 text-base rounded-md transition duration-200 ease-in-out mb-2 ${currentDifficultWordsIndex === 3 ? 'text-gray-400' : ''}`} // Gray out if at last index
                        title={currentDifficultWordsIndex === 2 ? 'Hard words' : (currentDifficultWordsIndex === 1 ? 'Medium words' : '')} // Tooltip for next button
                    >
                        <MoveRight /> {/* Use Lucid icon for next */}
                    </button>
                </div>
            </div>
            {/* Block 5: Defile Link Section */}
            {/* Conditional rendering for "Define with replay.one" section */}
            {searchQuery && ( // Use searchQuery state
                <div className="w-full">
                    <span>
                        Define <Link to={`https://www.replay.one/dictionary/${searchQuery}`} className="text-blue-600 hover:underline">{searchQuery}</Link> with replay.one.
                    </span>
                </div>
            )}
        </div>
    );
};

export default SearchPage;
